import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeStyle as _normalizeStyle, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

import { ImageBackgroundState, HotSpotData, ScrollOffset } from './types'
import SpotAnimation from '../general/SpotAnimation.vue'
import { useEventBus } from '@vueuse/core'
import { reactive, computed, onMounted, onUpdated } from 'vue'

interface Props {
  hotspots: Array<HotSpotData>
  activeHotspotId: string
  hoveredHotspotId: string
  position: string
  transform: string
  dir: string
  scrollProgress: number
  animationsVisible: boolean
  scrollOffset: ScrollOffset
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SpotAnimationContainer',
  props: {
    hotspots: null,
    activeHotspotId: null,
    hoveredHotspotId: null,
    position: null,
    transform: null,
    dir: null,
    scrollProgress: null,
    animationsVisible: { type: Boolean },
    scrollOffset: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  hotspots: Array<HotSpotData>
  activeHotspotId: string
  hoveredHotspotId: string
  position: string
  transform: string
  dir: string
  scrollProgress: number
  animationsVisible: boolean
  scrollOffset: ScrollOffset
}


const sectionLoadedBus = useEventBus('section-loaded')
const hotspotRefs = {}
const windowSize = {
  x: window.innerWidth,
  y: window.innerHeight,
}

const state: ImageBackgroundState = reactive({
  translate: '',
  scale: '',
  scaleRatio: 1,
  hotspotSize: 0,
  naturalWidth: 1920,
  naturalHeight: 1080,
  proxyPosition: '',
  offsetX: 0,
})

expose({ hotspotRefs })

// calculates the transform the browser is applying to
// the background image, and applies it to the hotspot container
// so the hotspots remain registered with it
const handleResize = () => {
  updateWindowSize()
  calculateTranslation()
  calculateZoom()
}

sectionLoadedBus.on(() => {
  calculateTranslation()
  calculateZoom()
})

const updateWindowSize = () => {
  windowSize.x = window.innerWidth
  windowSize.y = window.innerHeight
}

const position = computed(() => {
  const positions = props.position.split(' ')
  return {
    x: parseFloat(positions[0]) * 0.01,
    y: parseFloat(positions[1]) * 0.01,
  }
})

// calculate the translation to keep the hotspot container
// lined up with the background image
const calculateTranslation = () => {
  const elementFocalPoint = {
    x: windowSize.x * position.value.x,
    y: windowSize.y * position.value.y,
  }

  const imageFocalPoint = {
    x: state.naturalWidth * position.value.x,
    y: state.naturalHeight * position.value.y,
  }

  // position the focal point of the image within the element
  state.translate =
    'translate(' +
    (elementFocalPoint.x - imageFocalPoint.x - props.scrollOffset.offsetX) +
    'px, ' +
    (elementFocalPoint.y - imageFocalPoint.y) +
    'px)'
}

// calculate the scaling to keep the hotspot container
// lined up with the background image
const calculateZoom = () => {
  if (state.naturalWidth == 0 || state.naturalHeight == 0) {
    state.scaleRatio = 1
  } else {
    state.scaleRatio = Math.max(
      windowSize.x / state.naturalWidth,
      windowSize.y / state.naturalHeight
    )
  }

  // adjust scale to cover
  state.scale = 'scale(' + state.scaleRatio + ')'
  // calculate new dimensions of hotspots to maintain visible size no matter the actual scale
  state.hotspotSize = 38.0 * (1.0 / state.scaleRatio)
}

const getHotspotDataForId = (id) => {
  for (let i = 0; i < props.hotspots.length; i++) {
    if (props.hotspots[i].id == id) {
      return props.hotspots[i]
    }
  }
  return null
}

const mutedAnimationId = computed(() => {
  let result
  if (props.activeHotspotId != '') {
    result = getHotspotDataForId(props.activeHotspotId)?.animation.mute
  } else if (props.hoveredHotspotId != '') {
    result = getHotspotDataForId(props.hoveredHotspotId)?.animation.mute
  }
  if (result) {
    return result
  } else {
    return ''
  }
})

onUpdated(() => {
  calculateTranslation()
  calculateZoom()
})

onMounted(() => {
  handleResize()
})

return (_ctx: any,_cache: any) => {
  const _directive_resize = _resolveDirective("resize")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: "spot-animation-container absolute max-w-none left-0 top-0 pointer-events-none",
    style: _normalizeStyle({
      width: state.naturalWidth + 'px',
      height: state.naturalHeight + 'px',
      transformOrigin: props.position,
      transform: `${state.translate} ${state.scale}`,
    })
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.hotspots, (hotspot, index) => {
      return (_openBlock(), _createElementBlock("div", { key: index }, [
        (hotspot.animation)
          ? (_openBlock(), _createBlock(SpotAnimation, {
              key: 0,
              index: index,
              "data-index": index,
              hotspotData: hotspot,
              hoveredHotspotId: props.hoveredHotspotId,
              activeHotspotId: props.activeHotspotId,
              mutedAnimationId: _unref(mutedAnimationId),
              dir: props.dir,
              style: _normalizeStyle({
          opacity: props.animationsVisible ? 1 : 0,
        })
            }, null, 8, ["index", "data-index", "hotspotData", "hoveredHotspotId", "activeHotspotId", "mutedAnimationId", "dir", "style"]))
          : _createCommentVNode("", true)
      ]))
    }), 128))
  ], 4)), [
    [_directive_resize, handleResize]
  ])
}
}

})